import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS, DONUT_COLORS, DONUT_OTHER_COLOR } from '../typings/chart';
import { ChartPointModel }                               from '../typings/intelligence.chart-point';
import { ChartSeriesModel }                              from '../typings/intelligence.chart-series';
import { DonutSegmentModel }                             from '../typings/intelligence.donut-segment';
import { IntelligenceFormatService }                     from '../intelligence.format.service';
import { IntelligenceLowStockModel }                     from '../typings/intelligence.low-stock';
import { IntelligenceProductsModel }                     from '../typings/intelligence.products';
import { IntelligenceService }                           from '../intelligence.service';
import { IntelligenceTopProductModel }                   from '../typings/intelligence.top-product';
import { TableSortModel }                                from '../typings/intelligence.table-sort';

@Component({
  selector   : 'wor-intelligence-products-tab',
  template: require('./intelligence.products-tab.component.html')
})
export class ProductsTabComponent implements OnInit, OnDestroy {
  error = false;
  loading = true;

  incomeSegments : Array<DonutSegmentModel> = [];
  lowStock : Array<IntelligenceLowStockModel> = [];
  lowStockSort : TableSortModel = { direction: 'desc', field: '' };
  soldPoints : Array<ChartPointModel> = [];
  sort : TableSortModel = { direction: 'desc', field: 'revenue_ex_tax' };
  topProducts : Array<IntelligenceTopProductModel> = [];
  topProductTotals = { cost_ex_tax: 0, profit_ex_tax: 0, quantity: 0, revenue_ex_tax: 0 };

  soldSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'quantity', label: this.translateService.instant('INTELLIGENCE_SPACE.PRODUCTS.QUANTITY_SOLD') }
  ];

  private loadGeneration = 0;
  private subscription : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  count ( value : number ) : string {
    return this.intelligenceFormatService.count(value);
  }

  isIncomeEmpty () : boolean {
    return !this.incomeSegments.some(segment => segment.value > 0);
  }

  isLowStockEmpty () : boolean {
    return !this.lowStock.length;
  }

  isSoldEmpty () : boolean {
    return !this.soldPoints.some(point => point.values.quantity > 0);
  }

  isTopProductsEmpty () : boolean {
    return !this.topProducts.length;
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getProducts()
    .then(( data : IntelligenceProductsModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.soldPoints = ( data.products_sold || [] ).map(row => ({
        label : this.translateService.instant(`INTELLIGENCE_SPACE.PRODUCTS.TYPE_${ row.product_type.toUpperCase() }`),
        values: { quantity: row.quantity }
      }));

      const income = data.product_income || [];
      const kept = income.slice(0, DONUT_COLORS.length);
      const remainder = income.slice(DONUT_COLORS.length).reduce(( sum, row ) => sum + row.revenue_ex_tax, 0);

      this.incomeSegments = kept.map(( row, index ) => ({
        color: DONUT_COLORS[ index ],
        label: row.label,
        value: row.revenue_ex_tax
      }));

      if ( remainder > 0 ) {
        this.incomeSegments.push({
          color: DONUT_OTHER_COLOR,
          label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
          value: remainder
        });
      }

      this.topProducts = this.intelligenceFormatService.sortRows(data.top_products || [], this.sort.field, this.sort.direction);
      this.topProductTotals = this.topProducts.reduce(( totals, product ) => ({
        cost_ex_tax   : totals.cost_ex_tax + product.cost_ex_tax,
        profit_ex_tax : totals.profit_ex_tax + product.profit_ex_tax,
        quantity      : totals.quantity + product.quantity,
        revenue_ex_tax: totals.revenue_ex_tax + product.revenue_ex_tax
      }), { cost_ex_tax: 0, profit_ex_tax: 0, quantity: 0, revenue_ex_tax: 0 });

      this.lowStock = data.low_stock || [];
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  lowStockCsvRows () : Array<any> {
    return this.lowStock.map(product => ({
      item_code       : product.item_code,
      product_group   : product.product_group,
      quantity_on_hand: product.quantity_on_hand,
      minimum         : product.minimum
    }));
  }

  money ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  topProductsCsvRows () : Array<any> {
    return this.topProducts.map(product => ({
      item_code     : product.item_code,
      quantity      : product.quantity,
      revenue_ex_tax: product.revenue_ex_tax,
      cost_ex_tax   : product.cost_ex_tax,
      profit_ex_tax : product.profit_ex_tax
    }));
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.topProducts = this.intelligenceFormatService.sortRows(this.topProducts, this.sort.field, this.sort.direction);
  }

  sortLowStockBy ( field : string ) : void {
    this.lowStockSort = {
      direction: this.lowStockSort.field === field && this.lowStockSort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.lowStock = this.intelligenceFormatService.sortRows(this.lowStock, this.lowStockSort.field, this.lowStockSort.direction);
  }
}
